import { motion } from 'framer-motion';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../../lib/auth';
import { useTheme } from '../../lib/theme';
import { Button, cx } from '../ui/primitives';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  staffOnly?: boolean;
}

const RENTER_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: '◎' },
  { to: '/fleet', label: 'Flotila', icon: '⬡' },
  { to: '/reservations/new', label: 'Nová rezervace', icon: '＋' },
  { to: '/reservations', label: 'Moje rezervace', icon: '☰' },
  { to: '/notifications', label: 'Notifikace', icon: '◔' },
];

const STAFF_NAV: NavItem[] = [
  { to: '/dispatch', label: 'Dispatch board', icon: '▤', staffOnly: true },
  { to: '/approvals', label: 'Ke schválení', icon: '✓', staffOnly: true },
  { to: '/analytics', label: 'Vytížení a výnosy', icon: '◫', staffOnly: true },
  { to: '/notification-health', label: 'Notification health', icon: '◈', staffOnly: true },
  { to: '/audit', label: 'Audit log', icon: '⌇', staffOnly: true },
  { to: '/admin', label: 'Správa', icon: '⚙', staffOnly: true },
];

export function AppShell() {
  const { user, logout, isStaff } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = [...RENTER_NAV, ...(isStaff ? STAFF_NAV : [])];

  return (
    <div className="relative z-10 flex min-h-dvh flex-col lg:flex-row">
      {/* Sidebar (desktop) / drawer (mobile) */}
      <aside
        className={cx(
          'no-print z-30 shrink-0 border-[var(--border-subtle)] bg-[var(--surface-base)]/80 backdrop-blur-xl',
          'lg:sticky lg:top-0 lg:h-dvh lg:w-64 lg:border-r',
          menuOpen ? 'fixed inset-0' : 'hidden lg:block',
        )}
      >
        <div className="flex h-full flex-col gap-1 p-4">
          <div className="mb-5 flex items-center justify-between gap-2">
            <Brand />
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setMenuOpen(false)}
              aria-label="Zavřít menu"
            >
              ✕
            </Button>
          </div>

          <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto" aria-label="Hlavní navigace">
            {items.map((item, index) => (
              <div key={item.to}>
                {item.staffOnly === true && items[index - 1]?.staffOnly !== true && (
                  <p className="label-caps mt-4 mb-1.5 px-3">Operátor</p>
                )}
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    cx(
                      'flex items-center gap-2.5 rounded-[var(--radius)] px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-[var(--accent-dim)] font-medium text-[var(--accent)]'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--surface-glass-strong)] hover:text-[var(--text-primary)]',
                    )
                  }
                >
                  <span aria-hidden className="w-4 text-center opacity-70">
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              </div>
            ))}
          </nav>

          <div className="mt-4 border-t border-[var(--border-subtle)] pt-3">
            <div className="mb-2 px-1">
              <p className="truncate text-sm font-medium">{user?.displayName}</p>
              <p className="mono truncate text-[11px] text-[var(--text-muted)]">
                {user?.email} · {user?.role}
                {user && user.pilotLicenseClass !== 'NONE' ? ` · licence ${user.pilotLicenseClass}` : ''}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={toggle} className="flex-1">
                {theme === 'dark' ? '☀ Světlý' : '☾ Tmavý'}
              </Button>
              <Button variant="ghost" size="sm" onClick={logout} className="flex-1">
                Odhlásit
              </Button>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-20 flex items-center gap-3 border-b border-[var(--border-subtle)] bg-[var(--surface-base)]/70 px-4 py-3 backdrop-blur-xl lg:hidden">
          <Button variant="ghost" size="sm" onClick={() => setMenuOpen(true)} aria-label="Otevřít menu">
            ☰
          </Button>
          <Brand compact />
        </header>

        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
          className="mx-auto w-full max-w-[1500px] flex-1 px-4 py-5 sm:px-6 sm:py-7"
        >
          <Outlet />
        </motion.main>

        <footer className="no-print mx-auto w-full max-w-[1500px] px-4 pb-6 text-[11px] text-[var(--text-muted)] sm:px-6">
          SWI Airship Rentals · VŠB-TUO, SWI C01 · rezervační systém půjčovny vzducholodí
        </footer>
      </div>
    </div>
  );
}

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden
        className="grid size-8 place-items-center rounded-[var(--radius)] text-[15px]"
        style={{
          background: 'var(--accent-dim)',
          color: 'var(--accent)',
          border: '1px solid var(--border-strong)',
        }}
      >
        ⬭
      </span>
      <span className="leading-tight">
        <span className="block text-[13px] font-semibold tracking-tight">SWI Airship Rentals</span>
        {!compact && (
          <span className="mono block text-[10px] text-[var(--text-muted)]">
            charter operations
          </span>
        )}
      </span>
    </div>
  );
}
